import {useMemo, useState} from 'react'

import styles from './marketplace-navigation.module.css'

import {isFeatureEnabled} from '@github-ui/feature-flags'
import {CollapsibleNavList} from '@github-ui/layout-primitives/CollapsibleNavList'
import {useCollapsibleSidebarContext} from '@github-ui/layout-primitives/CollapsibleSidebar'
import {
  AiModelIcon,
  AppsIcon,
  ArrowUpRightIcon,
  CommandPaletteIcon,
  CopilotIcon,
  FlameIcon,
  ListUnorderedIcon,
  PlayIcon,
  PlusIcon,
} from '@primer/octicons-react'
import {Button, NavList} from '@primer/react'
import {Dialog} from '@primer/react/experimental'
import {useCategory} from '../contexts/CategoryContext'
import {useCopilotApp} from '../contexts/CopilotAppContext'
import {useSearchType} from '../contexts/SearchTypeContext'
import type {Category} from '../types'

export type Props = {
  categories: {
    apps: NavigationCategory[]
    actions: NavigationCategory[]
  }
  collapsible?: boolean
}

type NavigationCategory = Category & {
  children?: NavigationCategory[]
}

function flattenCategories(categories: NavigationCategory[] | undefined): NavigationCategory[] {
  if (!categories?.length) return []

  const flattened: NavigationCategory[] = []
  const seenSlugs = new Set<string>()

  const visit = (category: NavigationCategory) => {
    if (seenSlugs.has(category.slug)) return

    flattened.push(category)
    seenSlugs.add(category.slug)

    for (const child of category.children ?? []) visit(child)
  }

  for (const category of categories) visit(category)

  return flattened
}

function renderCategoryItems(
  categories: NavigationCategory[] | undefined,
  type: 'apps' | 'actions',
  currentType: string,
  currentNavItem: string,
) {
  const label = type === 'apps' ? 'All apps' : 'All actions'

  return [
    <NavList.Item
      key={`${type}-all`}
      href={`/marketplace?type=${type}`}
      aria-current={currentType === type && currentNavItem === '' ? 'page' : undefined}
    >
      {label}
    </NavList.Item>,
    ...flattenCategories(categories).map(category => (
      <NavList.Item
        key={`${type}-category-${category.slug}`}
        href={`/marketplace?type=${type}&category=${category.slug}`}
        aria-current={currentType === type && currentNavItem === category.slug ? 'page' : undefined}
      >
        {category.name}
      </NavList.Item>
    )),
  ]
}

function renderModelItems(currentType: string) {
  return [
    <NavList.Item
      key="models-catalog"
      href="/marketplace?type=models"
      aria-current={currentType === 'models' ? 'page' : undefined}
    >
      <NavList.LeadingVisual>
        <ListUnorderedIcon />
      </NavList.LeadingVisual>
      Catalog
    </NavList.Item>,
    <NavList.Item key="models-playground" href="/marketplace/models">
      <NavList.LeadingVisual>
        <CommandPaletteIcon />
      </NavList.LeadingVisual>
      Playground
      <NavList.TrailingVisual>
        <ArrowUpRightIcon />
      </NavList.TrailingVisual>
    </NavList.Item>,
  ]
}

function useNavigationSelection() {
  const {copilotApp} = useCopilotApp()
  const {type} = useSearchType()
  const {category} = useCategory()
  const hideCopilotExtensions = isFeatureEnabled('copilot_extensions_removal_on_marketplace')
  const hideGitHubModels = isFeatureEnabled('hide_github_models_ui')

  const currentNavItem = useMemo(() => {
    if (!type && !category && !copilotApp) {
      return 'featured'
    } else if (type === 'apps' && copilotApp === 'true') {
      return hideCopilotExtensions ? '' : 'copilot'
    } else if (category) {
      return category || ''
    }
    return ''
  }, [category, copilotApp, type, hideCopilotExtensions])

  const currentType = useMemo(() => {
    if (type && (hideCopilotExtensions || !copilotApp)) {
      if (type === 'apps') {
        return 'apps'
      } else if (type === 'actions') {
        return 'actions'
      } else if (type === 'models') {
        return 'models'
      }
    }
    return ''
  }, [copilotApp, type, hideCopilotExtensions])

  return {currentNavItem, currentType, hideCopilotExtensions, hideGitHubModels}
}

function LegacyMenu({categories}: Props) {
  const {currentNavItem, currentType, hideCopilotExtensions, hideGitHubModels} = useNavigationSelection()

  return (
    <NavList>
      <NavList.Item href="/marketplace" {...(currentNavItem === 'featured' ? {'aria-current': 'page'} : {})}>
        <NavList.LeadingVisual>
          <FlameIcon />
        </NavList.LeadingVisual>
        Featured
      </NavList.Item>
      {hideCopilotExtensions ? null : (
        <NavList.Item
          href="/marketplace?type=apps&copilot_app=true"
          {...(currentNavItem === 'copilot' ? {'aria-current': 'page'} : {})}
        >
          <NavList.LeadingVisual>
            <CopilotIcon />
          </NavList.LeadingVisual>
          Copilot
        </NavList.Item>
      )}
      {hideGitHubModels ? null : (
        <NavList.Item defaultOpen={currentType === 'models'}>
          <NavList.LeadingVisual>
            <AiModelIcon />
          </NavList.LeadingVisual>
          Models
          <NavList.SubNav>
            <NavList.Item href="/marketplace?type=models" aria-current={currentType === 'models' ? 'page' : undefined}>
              <NavList.LeadingVisual>
                <ListUnorderedIcon />
              </NavList.LeadingVisual>
              Catalog
            </NavList.Item>
            <NavList.Item href="/marketplace/models">
              <NavList.LeadingVisual>
                <CommandPaletteIcon />
              </NavList.LeadingVisual>
              Playground
              <NavList.TrailingVisual>
                <ArrowUpRightIcon />
              </NavList.TrailingVisual>
            </NavList.Item>
          </NavList.SubNav>
        </NavList.Item>
      )}
      <NavList.Item defaultOpen={currentType === 'apps'}>
        <NavList.LeadingVisual>
          <AppsIcon />
        </NavList.LeadingVisual>
        Apps
        <NavList.SubNav>
          <NavList.Item
            href={`/marketplace?type=apps`}
            {...(currentType === 'apps' && currentNavItem === '' ? {'aria-current': 'page'} : {})}
          >
            All apps
          </NavList.Item>
          {categories?.apps?.map(cat => (
            <NavList.Item
              key={`apps-category-${cat.slug}`}
              href={`/marketplace?type=apps&category=${cat.slug}`}
              {...(currentType === 'apps' && currentNavItem === cat.slug ? {'aria-current': 'page'} : {})}
            >
              {cat.name}
            </NavList.Item>
          ))}
        </NavList.SubNav>
      </NavList.Item>
      <NavList.Item defaultOpen={currentType === 'actions'}>
        <NavList.LeadingVisual>
          <PlayIcon />
        </NavList.LeadingVisual>
        Actions
        <NavList.SubNav>
          <NavList.Item
            href={`/marketplace?type=actions`}
            {...(currentType === 'actions' && currentNavItem === '' ? {'aria-current': 'page'} : {})}
          >
            All actions
          </NavList.Item>
          {categories?.actions?.map(cat => (
            <NavList.Item
              key={`actions-category-${cat.slug}`}
              href={`/marketplace?type=actions&category=${cat.slug}`}
              {...(currentType === 'actions' && currentNavItem === cat.slug ? {'aria-current': 'page'} : {})}
            >
              {cat.name}
            </NavList.Item>
          ))}
        </NavList.SubNav>
      </NavList.Item>
      <NavList.Divider />
      <NavList.Item href="/marketplace/new">
        <NavList.LeadingVisual>
          <PlusIcon />
        </NavList.LeadingVisual>
        Create a new extension
      </NavList.Item>
    </NavList>
  )
}

function CollapsibleMenu({categories}: Props) {
  const {isExpanded} = useCollapsibleSidebarContext()
  const {currentNavItem, currentType, hideCopilotExtensions, hideGitHubModels} = useNavigationSelection()
  const appItems = renderCategoryItems(categories.apps, 'apps', currentType, currentNavItem)
  const actionItems = renderCategoryItems(categories.actions, 'actions', currentType, currentNavItem)
  const modelItems = renderModelItems(currentType)

  return (
    <CollapsibleNavList aria-label="Marketplace navigation">
      <CollapsibleNavList.LinkItem
        to="/marketplace"
        label="Featured"
        icon={FlameIcon}
        aria-current={currentNavItem === 'featured' ? 'page' : undefined}
        forceHardNavigation
      />
      {hideCopilotExtensions ? null : (
        <CollapsibleNavList.LinkItem
          to="/marketplace?type=apps&copilot_app=true"
          label="Copilot"
          icon={CopilotIcon}
          aria-current={currentNavItem === 'copilot' ? 'page' : undefined}
          forceHardNavigation
        />
      )}
      {hideGitHubModels ? null : (
        <CollapsibleNavList.Group
          title={isExpanded ? undefined : 'Models'}
          aria-label="Models"
          icon={AiModelIcon}
          headingLevel="h2"
        >
          {isExpanded ? (
            <CollapsibleNavList.Item defaultOpen={currentType === 'models'}>
              <CollapsibleNavList.LeadingVisual>
                <AiModelIcon />
              </CollapsibleNavList.LeadingVisual>
              Models
              <CollapsibleNavList.SubNav>{modelItems}</CollapsibleNavList.SubNav>
            </CollapsibleNavList.Item>
          ) : (
            modelItems
          )}
        </CollapsibleNavList.Group>
      )}
      <CollapsibleNavList.Group
        title={isExpanded ? undefined : 'Apps'}
        aria-label="Apps"
        icon={AppsIcon}
        headingLevel="h2"
      >
        {isExpanded ? (
          <CollapsibleNavList.Item defaultOpen={currentType === 'apps'}>
            <CollapsibleNavList.LeadingVisual>
              <AppsIcon />
            </CollapsibleNavList.LeadingVisual>
            Apps
            <CollapsibleNavList.SubNav>{appItems}</CollapsibleNavList.SubNav>
          </CollapsibleNavList.Item>
        ) : (
          appItems
        )}
      </CollapsibleNavList.Group>
      <CollapsibleNavList.Group
        title={isExpanded ? undefined : 'Actions'}
        aria-label="Actions"
        icon={PlayIcon}
        headingLevel="h2"
      >
        {isExpanded ? (
          <CollapsibleNavList.Item defaultOpen={currentType === 'actions'}>
            <CollapsibleNavList.LeadingVisual>
              <PlayIcon />
            </CollapsibleNavList.LeadingVisual>
            Actions
            <CollapsibleNavList.SubNav>{actionItems}</CollapsibleNavList.SubNav>
          </CollapsibleNavList.Item>
        ) : (
          actionItems
        )}
      </CollapsibleNavList.Group>
      <CollapsibleNavList.Divider />
      <CollapsibleNavList.LinkItem
        to="/marketplace/new"
        label="Create a new extension"
        icon={PlusIcon}
        forceHardNavigation
      />
    </CollapsibleNavList>
  )
}

function LegacyNavigation({categories}: Props) {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const onMenuClose = () => setIsMenuOpen(false)

  return (
    <>
      <div className="hide-sm hide-md overflow-hidden">
        <LegacyMenu categories={categories} />
      </div>

      <Button block className="hide-lg hide-xl" onClick={() => setIsMenuOpen(true)}>
        Menu
      </Button>
      {isMenuOpen && (
        <Dialog title="Menu" onClose={onMenuClose} position={{narrow: 'bottom'}}>
          <div className={styles['negative-margin']}>
            <LegacyMenu categories={categories} />
          </div>
        </Dialog>
      )}
    </>
  )
}

export default function MarketplaceNavigation({categories, collapsible = false}: Props) {
  return collapsible ? <CollapsibleMenu categories={categories} /> : <LegacyNavigation categories={categories} />
}
